import React, { SyntheticEvent, useEffect, useState } from "react";
import {
    Button,
    Card,
    Header,
    Icon,
    Modal,
    ModalActions,
    ModalContent,
} from "semantic-ui-react";
import { deleteProduct, getProducts } from "../../services/products.service";
import Product from "../../types/entities/Product";
import LuxonDataTable from "../../components/LuxonDataTable";
import { toast } from "react-toastify";
import RootTemplate from "../../components/RootTemplate";
import PageHeader from "../../components/PageHeader";
import CreateProductModal from "./modals/CreateProductModal";
import UpdateProductModal from "./modals/UpdateProductModal";

const ProductsList = () => {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [openDelete, setOpenDelete] = useState(false);
    const [productToDelete, setProductToDelete] = useState<undefined | string>(undefined);

    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);

    const columns = [
        {
            name: "Codigo (SKU)",
            selector: (row: Product) => row.sku,
        },
        {
            name: "Nombre",
            selector: (row: Product) => row.name,
        },
        {
            name: "Tiempo de produccion",
            selector: (row: Product) => `${row.productionTimeMin} - ${row.productionTimeMax} dias`,
        },
        {
            name: "Dia de recordatorio",
            selector: (row: Product) => row.reminderTime,
        },
        {
            name: "Acciones",
            button: true,
            cell: (row: Product) => (
                <>
                    <Button color="yellow" icon size="mini" onClick={(event) => handleOpenUpdate(event, row)}>
                        <Icon name="pencil" />
                    </Button>
                    <Button color="red" icon size="mini" onClick={(event) => handleOpenDelete(event, row.id)} content={row.id}>
                        <Icon name="trash" />
                    </Button>
                </>
            ),
        },
    ];

    const handleOpenUpdate = (event: SyntheticEvent, product: Product) => {
        event.preventDefault();
        setSelectedProduct(product);
        setOpenUpdateModal(true);
    };

    const fetchData = async (page: number) => {
        setLoading(true);
        try {
            const response = await getProducts(perPage, page - 1);
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
        } catch (error) {
            console.error("Error al obtener la informacion:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        fetchData(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        try {
            const response = await getProducts(newPerPage, page - 1);
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
            setPerPage(newPerPage);
        } catch (error) {
            console.error("Error al obtener la informacion:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line
    }, []);

    const handleOpenDelete = (event: SyntheticEvent, advisorId: string) => {
        event.preventDefault();
        setOpenDelete(true);
        setProductToDelete(advisorId);
    };

    const handleCancelDelete = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenDelete(false);
        setProductToDelete(undefined);
    };

    const handleDelete = async (event: SyntheticEvent) => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await deleteProduct(productToDelete);
            if (response?.status === 204) {
                fetchData(1)
                toast.success("El producto ha sido eliminado exitosamente.");
            } else {
                throw new Error("Error al eliminar producto.");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setOpenDelete(false);
            setProductToDelete(undefined);
            setLoading(false);
        }
    };

    return (
        <RootTemplate>

            <PageHeader
                title="Productos"
                subtitle="Catalogo de productos"
                actionComponents={
                    <Button onClick={() => setOpenCreateModal(true)} floated="right">
                        Nuevo producto
                    </Button>
                }
            />

            <Card fluid>

                <LuxonDataTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    totalRows={totalRows}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                />
            </Card>

            <Modal
                closeOnDimmerClick
                closeIcon={true}
                size="mini"
                onClose={(event) => handleCancelDelete(event)}
                open={openDelete} >
                <Header
                    icon="warning circle"
                    content="Favor de validar acción"
                />
                <ModalContent>
                    <p>
                        Una vez eliminado este producto, sus datos no podran ser recuperados. Si estas deacuerdo da click en Eliminar.
                    </p>
                </ModalContent>
                <ModalActions>
                    <Button onClick={(event) => handleCancelDelete(event)}>
                        <Icon name="cancel" /> Cancelar
                    </Button>
                    <Button color="green" onClick={(event) => handleDelete(event)}>
                        <Icon name="checkmark" /> Eliminar
                    </Button>
                </ModalActions>
            </Modal>

            <CreateProductModal
                open={openCreateModal}
                onClose={() => setOpenCreateModal(false)}
                onRefreshData={() => fetchData(1)}
            />

            <UpdateProductModal
                open={openUpdateModal}
                product={selectedProduct}
                onClose={() => setOpenUpdateModal(false)}
                onRefreshData={() => fetchData(1)}
            />

        </RootTemplate>
    );
};

export default ProductsList;
